"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

const Breadcrumb = () => {
  const pathname = usePathname();

  // Don't show breadcrumb on home page
  if (!pathname || pathname === "/") {
    return null;
  }

  // Split the pathname and filter out empty segments
  const segments = pathname.split("/").filter(Boolean);

  // Function to format segment names (convert kebab-case to Title Case)
  const formatSegmentName = (segment) => {
    return decodeURIComponent(segment)
      .replace(/[-_]/g, " ")
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const ChevronRightIcon = () => (
    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );

  return (
    <nav 
      className="absolute top-[60px] sm:top-[65px] xl:top-[80px] left-0 right-0 z-40"
      aria-label="Breadcrumb navigation"
    >
      <div className="w-full mx-auto px-4">
        <div className="flex items-center py-2 text-xs overflow-x-auto scrollbar-hide">
          {/* Home link with icon */}
          <Link 
            href="/" 
            className="flex items-center space-x-1 px-1 py-1 text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded-md sm:rounded-lg transition-all duration-200 group whitespace-nowrap flex-shrink-0"
            style={{
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <span className="font-medium">Home</span>
          </Link>

          {/* Dynamic segments */}
          {segments.map((segment, index) => {
            const href = "/" + segments.slice(0, index + 1).join("/");
            const isLast = index === segments.length - 1;
            const segmentName = formatSegmentName(segment);

            return (
              <div key={`${segment}-${index}`} className="flex items-center flex-shrink-0">
                {/* Separator */}
                <div className="">
                  <ChevronRightIcon />
                </div>
                
                {isLast ? (
                  // Last segment - not clickable, highlighted
                  <div 
                    className="flex items-center space-x-1 px-1 py-1 bg-teal-50 text-teal-700 rounded-md lg:rounded-lg border border-teal-200 whitespace-nowrap"
                    style={{
                      backgroundColor: '#f0fdfa',
                      borderColor: '#5eead4'
                    }}
                  >
                    <span className="font-semibold text-xs truncate max-w-[120px]">
                      {segmentName}
                    </span>
                  </div>
                ) : (
                  // Intermediate segments - clickable
                  <Link 
                    href={href} 
                    className="flex items-center space-x-1 px-1 py-1 text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded-md lg:rounded-lg transition-all duration-200 group whitespace-nowrap"
                    style={{
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    <span className="font-medium group-hover:text-teal-600 text-xs truncate max-w-[120px]">
                      {segmentName}
                    </span>
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Custom scrollbar styles for overflow */}
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </nav>
  );
};

export default Breadcrumb;